/**
 * `HealthView` — the admin's "System Health" / vitals board.
 *
 * Backs `GET /internal/admin/health/checks`. The endpoint runs a curated
 * set of LIVE probes (DB reachability, edge JWKS reachability, rollup vs
 * live recompute reconciliation, audit_record pipeline tells) and returns
 * an `AdminHealthReport` whose `checks[]` carry independent pass/warn/fail
 * status — a single check's failure never 500s the whole endpoint.
 *
 * Layout (mirrors `MetricsView` shell — mono kicker + serif title +
 * caption, then content):
 *   1. Page header — kicker + "System Health" title + "as of {generated_at}
 *      ET · ran in {duration_ms} ms" subline.
 *   2. Run-controls row — summary counts (mint pass / amber warn / coral
 *      fail) on the left; a primary "Run checks" button on the right
 *      (full-width on mobile via a column→row flip at sm:).
 *   3. Loading state — eight skeleton check rows so the board doesn't
 *      collapse to "Running…" text and then jump back to full height.
 *   4. Error state — coral surfaced server reason (`ApiError.server_reason`
 *      when present, else the error kind).
 *   5. Ready state — one Card per check, sorted FAILS FIRST so problems
 *      surface at the top. Each row carries:
 *        - status pill (mint=pass / amber=warn / coral=fail)
 *        - the check label
 *        - a small category chip (infra / data / pipeline)
 *        - the `detail` text (sanitized server-side; safe to render raw)
 *        - the `duration_ms` in JetBrains Mono ("412 ms")
 *
 * Auto-runs once on mount; subsequent runs are user-initiated via the
 * "Run checks" button (disabled + "Running…" copy while in flight).
 *
 * Mobile:
 *   - Run-controls row stacks (counts above, full-width button below).
 *   - Each check card stacks: status + label on top, category + duration
 *     under it, detail under that. Tap targets clear the 40px line.
 */

import { Activity, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type {
  AdminHealthCategory,
  AdminHealthCheck,
  AdminHealthReport,
  AdminHealthStatus,
} from '@sill/types';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { EscalateDialog } from '../../components/atoms/EscalateDialog.js';
import type { EscalateContext } from '../../components/atoms/EscalateDialog.js';
import { ExportMenu } from '../../components/atoms/ExportMenu.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { downloadHealthJson, downloadHealthMarkdown } from '../../lib/report-export.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

type HealthState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; report: AdminHealthReport };

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.server_reason !== undefined && err.server_reason.length > 0) {
      return err.server_reason;
    }
    return err.kind;
  }
  return 'Unexpected error';
}

export function HealthView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [state, setState] = useState<HealthState>({ kind: 'loading' });
  // `running` is the in-flight flag the button drives off of — distinct
  // from `state.kind === 'loading'` because we want subsequent runs to
  // keep the previous report visible (the board doesn't blank between
  // runs) while still disabling the button + flipping the label.
  const [running, setRunning] = useState(false);
  const [escalate, setEscalate] = useState<EscalateContext | null>(null);

  const runChecks = useCallback(
    async (firstLoad: boolean): Promise<void> => {
      setRunning(true);
      if (firstLoad) {
        setState({ kind: 'loading' });
      }
      try {
        const report = await api.getHealthChecks();
        setState({ kind: 'ready', report });
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'unauthorized') {
          // The http-client's on_unauthorized hook already routed to
          // the login view; do not clobber the state with an error here.
          return;
        }
        setState({ kind: 'error', message: errorMessage(err) });
      } finally {
        setRunning(false);
      }
    },
    [api],
  );

  // Auto-run once on mount.
  useEffect(() => {
    void runChecks(true);
  }, [runChecks]);

  const handleRun = useCallback((): void => {
    void runChecks(false);
  }, [runChecks]);

  const handleEscalateOpen = useCallback((check: AdminHealthCheck): void => {
    // `detail` is omitted (not set to undefined) when empty so the
    // strict exactOptionalPropertyTypes contract on EscalateContext
    // accepts the payload.
    const base: EscalateContext = {
      kind: 'health_check',
      ref: check.id,
      summary: check.label,
      status: check.status,
    };
    setEscalate(
      check.detail.length > 0 ? { ...base, detail: check.detail } : base,
    );
  }, []);

  const handleEscalateClose = useCallback((): void => {
    setEscalate(null);
  }, []);

  const summary =
    state.kind === 'ready' ? state.report.summary : { pass: 0, warn: 0, fail: 0 };
  const generatedAt = state.kind === 'ready' ? state.report.generated_at : null;
  const durationMs = state.kind === 'ready' ? state.report.duration_ms : null;
  const report = state.kind === 'ready' ? state.report : null;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      {/* Kicker + title */}
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Vitals
      </div>
      <div className="flex flex-wrap items-end gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="min-w-0">
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(28px, 6vw, 44px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
              margin: 0,
            }}
          >
            System Health
          </h1>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.5,
              marginTop: 10,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {generatedAt !== null ? (
              <>
                Last run {formatIsoCompact(generatedAt)}
                {durationMs !== null ? (
                  <>
                    {' · '}
                    <span style={{ color: C.dim }}>ran in {durationMs} ms</span>
                  </>
                ) : null}
              </>
            ) : state.kind === 'error' ? (
              <>Last run failed</>
            ) : (
              <>Running checks…</>
            )}
          </div>
          {/* One-line view intro. Plain language; explains what this
              board is for so staff (and the occasional founder) don't
              have to infer it from the check list. */}
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.body,
              marginTop: 10,
              lineHeight: 1.5,
              maxWidth: 640,
            }}
          >
            Live probes of the systems Sill depends on — database, edge
            signing keys, the origin API, and the discovery pipeline.
            Each card shows the current result; expand{' '}
            <span style={{ fontFamily: monoFont, fontSize: 12 }}>
              What is this?
            </span>{' '}
            for what the check verifies and why it matters.
          </div>
        </div>
      </div>

      {/* Run-controls row — counts (left) + Export + Run buttons (right).
          Stacks on mobile (counts above, full-width buttons below). */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
        <SummaryCounts
          pass={summary.pass}
          warn={summary.warn}
          fail={summary.fail}
          dim={state.kind !== 'ready'}
        />
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <ExportMenu
            disabled={report === null}
            onExportMarkdown={() => {
              if (report !== null) downloadHealthMarkdown(report);
            }}
            onExportJson={() => {
              if (report !== null) downloadHealthJson(report);
            }}
          />
          <RunButton running={running} disabled={running} onClick={handleRun} />
        </div>
      </div>

      {/* Board */}
      {state.kind === 'loading' ? (
        <LoadingBoard />
      ) : state.kind === 'error' ? (
        <ErrorBoard message={state.message} />
      ) : (
        <CheckBoard checks={state.report.checks} onEscalate={handleEscalateOpen} />
      )}

      {/* Escalate dialog */}
      {escalate !== null ? (
        <EscalateDialog
          open={escalate !== null}
          context={escalate}
          onClose={handleEscalateClose}
        />
      ) : null}
    </div>
  );
}

/**
 * Big-number summary counts: PASS / WARN / FAIL. Mono digits in the
 * semantic colors. `dim` greys the row out while we don't have a
 * report (loading / error) — keeps the layout stable.
 */
function SummaryCounts({
  pass,
  warn,
  fail,
  dim,
}: {
  pass: number;
  warn: number;
  fail: number;
  dim: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex items-baseline flex-wrap gap-x-5 gap-y-1">
      <CountChip count={pass} label="passing" color={dim ? C.dim : C.mintFg} />
      <CountChip count={warn} label="warnings" color={dim ? C.dim : C.amberFg} />
      <CountChip count={fail} label="failing" color={dim ? C.dim : C.coralFg} />
    </div>
  );
}

function CountChip({
  count,
  label,
  color,
}: {
  count: number;
  label: string;
  color: string;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 22,
          fontWeight: 500,
          color,
          letterSpacing: -0.5,
          lineHeight: 1,
        }}
      >
        {count}
      </span>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * The "Run checks" button. Primary (lime-on-ink), full-width on mobile,
 * inline on sm+. Disabled + "Running…" copy while a request is in flight.
 */
function RunButton({
  running,
  disabled,
  onClick,
}: {
  running: boolean;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  const isDisabled = disabled || running;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-label={running ? 'Running checks' : 'Run checks'}
      aria-busy={running}
      className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
      style={{
        background: isDisabled ? C.surface2 : C.lime,
        color: isDisabled ? C.muted : C.ink,
        border: `1px solid ${isDisabled ? C.border : C.lime}`,
        borderRadius: 2,
        padding: '10px 16px',
        minHeight: 40,
        fontFamily: monoFont,
        fontSize: 12,
        letterSpacing: 1.2,
        fontWeight: 500,
        textTransform: 'uppercase',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'opacity 120ms ease',
        opacity: isDisabled && !running ? 0.7 : 1,
      }}
    >
      <Activity size={14} aria-hidden="true" />
      {running ? 'Running…' : 'Run checks'}
    </button>
  );
}

function LoadingBoard(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 8 }, (_, i) => (
        <SkeletonCheckRow key={`skel-${i}`} />
      ))}
    </div>
  );
}

function SkeletonCheckRow(): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 16 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.dim,
          letterSpacing: 1,
        }}
      >
        Running…
      </div>
    </Card>
  );
}

function ErrorBoard({ message }: { message: string }): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 18 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Health endpoint unavailable
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 13,
          color: C.coralFg,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {message}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          marginTop: 8,
          lineHeight: 1.5,
        }}
      >
        Re-running the checks may recover; if the error persists the API
        host itself is the problem (the checks endpoint is fail-soft per
        check, so a hard error here means the endpoint never returned).
      </div>
    </Card>
  );
}

function CheckBoard({
  checks,
  onEscalate,
}: {
  checks: ReadonlyArray<AdminHealthCheck>;
  onEscalate: (check: AdminHealthCheck) => void;
}): JSX.Element {
  const C = useC();

  if (checks.length === 0) {
    return (
      <Card style={{ padding: 18 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.muted,
            letterSpacing: 0.5,
          }}
        >
          No checks were returned by the server.
        </div>
      </Card>
    );
  }

  // Failing first, then warn, then pass — problems surface at the top
  // (CLAUDE.md voice: honesty over polish; a coral row should be the
  // first thing a staff member sees). Stable secondary sort by `id`
  // so repeated runs don't reshuffle same-status rows.
  const sorted = [...checks].sort((a, b) => {
    const sa = STATUS_RANK[a.status];
    const sb = STATUS_RANK[b.status];
    if (sa !== sb) return sa - sb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((check) => (
        <CheckRow key={check.id} check={check} onEscalate={onEscalate} />
      ))}
    </div>
  );
}

function CheckRow({
  check,
  onEscalate,
}: {
  check: AdminHealthCheck;
  onEscalate: (check: AdminHealthCheck) => void;
}): JSX.Element {
  const C = useC();
  const tone = statusToTone(check.status);

  // Collapsed by default — the live detail is the answer to "what's
  // happening now"; the explanation answers "what does this mean"
  // and only a fraction of viewings need it.
  const [expanded, setExpanded] = useState(false);
  const explanation = explanationFor(check.id);
  const panelId = `health-check-explain-${check.id}`;
  const toggle = useCallback((): void => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Card style={{ padding: 16 }}>
      {/* Top row: pill + label + category + duration.
          On mobile: pill+label on the first line, category+duration on
          the second; on sm+ everything sits in a single row. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <Pill tone={tone}>{check.status}</Pill>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              fontWeight: 500,
              color: C.text,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {check.label}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CategoryChip category={check.category} />
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            {check.duration_ms} ms
          </span>
          {check.status === 'fail' || check.status === 'warn' ? (
            <EscalateButton status={check.status} onClick={() => onEscalate(check)} />
          ) : null}
        </div>
      </div>

      {/* Detail line — sanitized server-side, safe to render raw. */}
      {check.detail.length > 0 ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: check.status === 'fail' ? C.coralFg : C.body,
            marginTop: 10,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {check.detail}
        </div>
      ) : null}

      {/* "What is this?" toggle row — small mono kicker on the left,
          stable machine id on the right (matches the old caption
          position so it doesn't move with the toggle). */}
      <div
        className="flex items-center justify-between gap-2 flex-wrap"
        style={{ marginTop: 10 }}
      >
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          className="inline-flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: 'none',
            padding: '8px 0',
            minHeight: 40,
            color: C.muted,
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            // The button itself owns the focus ring; keep the row
            // calm by suppressing the default browser outline and
            // relying on the global focus-visible styles.
            outline: 'none',
          }}
        >
          <span>{expanded ? 'Hide' : 'What is this?'}</span>
          <ChevronDown
            size={12}
            aria-hidden="true"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 120ms ease',
            }}
          />
        </button>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.dim,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          {check.id}
        </span>
      </div>

      {/* Explanation panel — collapsed by default. Plain language,
          distinct from the live `detail` above. `hidden` keeps the
          content out of the a11y tree when collapsed (matches the
          aria-expanded=false on the toggle). */}
      <div
        id={panelId}
        hidden={!expanded}
        style={{
          marginTop: expanded ? 10 : 0,
          paddingTop: expanded ? 10 : 0,
          borderTop: expanded ? `1px solid ${C.borderSoft}` : 'none',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          What this checks / why it matters
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.body,
            lineHeight: 1.55,
            wordBreak: 'break-word',
          }}
        >
          {explanation}
        </div>
      </div>
    </Card>
  );
}

function EscalateButton({
  status,
  onClick,
}: {
  status: AdminHealthStatus;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  const isFail = status === 'fail';
  const bg = isFail ? C.coralDim : C.amberDim;
  const fg = isFail ? C.coralFg : C.amberFg;
  const bd = isFail ? 'rgba(255,107,71,0.3)' : 'rgba(255,182,39,0.3)';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Escalate to GitHub issue"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: fg,
        background: bg,
        border: `1px solid ${bd}`,
        borderRadius: 2,
        padding: '6px 10px',
        minHeight: 32,
        letterSpacing: 1,
        fontWeight: 500,
        textTransform: 'uppercase',
        cursor: 'pointer',
      }}
    >
      Escalate
    </button>
  );
}

function CategoryChip({ category }: { category: AdminHealthCategory }): JSX.Element {
  const C = useC();
  return (
    <span
      className="inline-flex items-center"
      style={{
        background: C.surface2,
        color: C.muted,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: '2px 6px',
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 1.2,
        fontWeight: 500,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {category}
    </span>
  );
}

const STATUS_RANK: Record<AdminHealthStatus, number> = {
  fail: 0,
  warn: 1,
  pass: 2,
};

/**
 * Static per-check explanations, keyed by `check.id`. This is
 * intentionally NOT shipped from the server — the live `detail`
 * is the *current result*; this is the *durable meaning* of the
 * check, and lives next to the UI that renders it.
 *
 * If the server adds a new check.id, the row still renders — the
 * fallback ("No description available.") makes the addition visible
 * without breaking the board. Add an entry here when the new check
 * stabilizes.
 */
const CHECK_EXPLANATIONS: Record<string, string> = {
  db_reachable:
    "Confirms the API can reach the primary database with a simple query. If this fails, the platform can't read or write anything — it's the most critical check.",
  edge_jwks:
    'Fetches the public signing keys the edge publishes (JWKS). These let anyone independently verify Sill’s signatures. A failure means signature verification or the edge worker may be impaired.',
  api_healthz:
    "The origin API's own liveness endpoint. Confirms the core API is up and serving requests.",
  metrics_reconcile:
    'Recomputes one recent day’s metrics live and compares them to the stored rollup. Catches the dashboard showing numbers that have drifted from the raw data — the class of bug behind inflated counts.',
  recent_pipeline_activity:
    'Counts real agent events (audit records) in the last 24 hours. Confirms the discovery/mandate pipeline is actually processing traffic. A warning means it’s quiet, not necessarily broken.',
};

const EXPLANATION_FALLBACK = 'No description available.';

function explanationFor(id: string): string {
  return CHECK_EXPLANATIONS[id] ?? EXPLANATION_FALLBACK;
}

function statusToTone(status: AdminHealthStatus): PillTone {
  switch (status) {
    case 'pass':
      return 'mint';
    case 'warn':
      return 'amber';
    case 'fail':
      return 'coral';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
